import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const InsightsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = ['All', 'AI & ML', 'Cloud', 'Cybersecurity', 'Digital Transformation', 'Industry Trends'];

  const insights = [
    {
      id: 1,
      title: 'The Future of AI in Enterprise: Trends and Predictions for 2024',
      category: 'AI & ML',
      author: 'Dr. Sarah Chen',
      date: '2024-01-15',
      readTime: '8 min read',
      excerpt: 'Explore the latest trends in enterprise AI adoption and what organizations need to know to stay competitive in the AI-driven future.',
      image: '/api/placeholder/400/250'
    },
    {
      id: 2,
      title: 'Zero Trust Security: A Comprehensive Guide for Modern Enterprises',
      category: 'Cybersecurity',
      author: 'Michael Rodriguez',
      date: '2024-01-12',
      readTime: '12 min read',
      excerpt: 'Learn how to implement zero trust architecture and protect your organization from evolving cyber threats.',
      image: '/api/placeholder/400/250'
    },
    {
      id: 3,
      title: 'Cloud Migration Strategies: Lessons from 500+ Enterprise Transformations',
      category: 'Cloud',
      author: 'Jennifer Park',
      date: '2024-01-10',
      readTime: '10 min read',
      excerpt: 'Discover proven strategies and best practices for successful cloud migration based on real-world enterprise experiences.',
      image: '/api/placeholder/400/250'
    },
    {
      id: 4,
      title: 'Digital Transformation ROI: Measuring Success in the Modern Enterprise',
      category: 'Digital Transformation',
      author: 'David Thompson',
      date: '2024-01-08',
      readTime: '6 min read',
      excerpt: 'Understanding how to measure and maximize ROI from digital transformation initiatives.',
      image: '/api/placeholder/400/250'
    },
    {
      id: 5,
      title: 'Industry 4.0: Manufacturing\'s Digital Revolution',
      category: 'Industry Trends',
      author: 'Lisa Wang',
      date: '2024-01-05',
      readTime: '9 min read',
      excerpt: 'How smart manufacturing and IoT are transforming the industrial landscape.',
      image: '/api/placeholder/400/250'
    },
    {
      id: 6,
      title: 'Machine Learning Operations: Building Scalable ML Pipelines',
      category: 'AI & ML',
      author: 'Alex Kumar',
      date: '2024-01-03',
      readTime: '11 min read',
      excerpt: 'Best practices for implementing MLOps and scaling machine learning operations in enterprise environments.',
      image: '/api/placeholder/400/250'
    }
  ];

  const filteredInsights = insights.filter(insight => {
    const matchesCategory = selectedCategory === 'All' || insight.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      insight.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.author.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <Helmet>
        <title>Insights & Research - Technology Trends | AuVSys</title>
        <meta name="description" content="Stay ahead with the latest technology trends, research, and thought leadership from AuVSys experts." />
      </Helmet>
      
      <div className="insights-page">
        <div className="container">
        <motion.div 
          className="page-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>Insights & Research</h1>
          <p>Stay ahead with the latest trends, research, and thought leadership in technology and digital transformation.</p>
        </motion.div>

        <div className="insights-filters">
          <div className="filter-buttons">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          
          <div className="search-box">
            <Search size={20} />
            <input 
              type="text" 
              placeholder="Search insights..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="insights-grid">
          {filteredInsights.map((insight, index) => (
            <motion.article
              key={insight.id}
              className="insight-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <div className="insight-image">
                <img src={insight.image} alt={insight.title} />
                <span className="insight-category">{insight.category}</span>
              </div>
              
              <div className="insight-content">
                <h3>{insight.title}</h3>
                <p>{insight.excerpt}</p>
                
                <div className="insight-meta">
                  <div className="meta-left">
                    <div className="meta-item">
                      <User size={14} />
                      <span>{insight.author}</span>
                    </div>
                    <div className="meta-item">
                      <Calendar size={14} />
                      <span>{new Date(insight.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <span className="read-time">{insight.readTime}</span>
                </div>
                
                <Link to={`/insights/${insight.id}`} className="read-more">
                  Read More <ArrowRight size={16} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="newsletter-signup">
          <h3>Stay Updated</h3>
          <p>Subscribe to our newsletter for the latest insights and industry trends.</p>
          <div className="newsletter-form">
            <input type="email" placeholder="Enter your email" />
            <button>Subscribe</button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default InsightsPage;